import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { getApiInstance } from 'services/Api';

// -----------------------------------------------------------------------------
// Thunk
// -----------------------------------------------------------------------------
// Exports the policy objects as CSV. We fetch it as a Blob so the UI can trigger a download.
export const fetchPolicyObjectsExport = createAsyncThunk<{ data: Blob; fileName: string }, { policyNo: string }>(
	'policyObjectsExport/fetchPolicyObjectsExport',
	async ({ policyNo }) => {
		const apiInstance = await getApiInstance('/commercial-selfservice/');

		// Force responseType 'blob' and correct Accept so backend returns CSV.
		const response = await apiInstance.bffApiServiceLayer.exportPolicyObjects(policyNo, {
			responseType: 'blob' as any,
			headers: { Accept: 'text/csv' },
		});

		const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
		return {
			data: blob,
			fileName: `policy_objects_${policyNo}.csv`,
		};
	}
);

// -----------------------------------------------------------------------------
// State
// -----------------------------------------------------------------------------
interface PolicyObjectsExportState {
	exportBlob: Blob | null;
	status: HttpStatusCode | null;
	error: string | null;
}

const initialState: PolicyObjectsExportState = {
	exportBlob: null,
	status: HttpStatusCode.Idle,
	error: null,
};

// -----------------------------------------------------------------------------
// Slice
// -----------------------------------------------------------------------------
const policyObjectsExportSlice = createSlice({
	name: 'policyObjectsExport',
	initialState,
	reducers: {
		clearPolicyObjectsExport(state) {
			state.exportBlob = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchPolicyObjectsExport.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchPolicyObjectsExport.fulfilled, (state, action) => {
				state.status = HttpStatusCode.OK;
				state.exportBlob = action.payload.data;
			})
			.addCase(fetchPolicyObjectsExport.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Medarbejderliste download fejlede';
			});
	},
});

export const { clearPolicyObjectsExport } = policyObjectsExportSlice.actions;
export default policyObjectsExportSlice.reducer;
